import ic_backspace from '@material-design-icons/svg/outlined/backspace.svg?raw';
import ic_trash from '@material-design-icons/svg/outlined/delete_outline.svg?raw';
import ic_search from '@material-design-icons/svg/outlined/search.svg?raw';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { matchesSearchQuery } from '../../../core/services/global-search/utils/filter.ts';
import styles from './GlobalSearch.scss?inline';
import { GlobalSearchStorage } from './storage.ts';
import { ESearchCategory, IGlobalSearchSelectEventDetail, ISearchProvider, ISearchResult } from './types';

interface VisibleResultItem {
    path: string;
    result: ISearchResult;
    depth: number;
    parentPath?: string;
    hasChildren: boolean;
    expanded: boolean;
}

@customElement('app-global-search')
export class GlobalSearchComponent extends LitElement {
    @state() public active = false;
    @state() private query = '';
    @state() private results: ISearchResult[] = [];
    @state() private selectedIndex = 0;
    @state() private recentSearches: string[] = [];
    @state() private expandedPaths: string[] = [];
    private lastSelectedPath: string | null = null;

    static styles = [unsafeCSS(styles)];

    /**
     * Proveedores de búsqueda inyectados
     */
    @property({ type: Array })
    providers: ISearchProvider[] = [];

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('keydown', this.handleGlobalKeydown);
    }

    disconnectedCallback() {
        window.removeEventListener('keydown', this.handleGlobalKeydown);
        super.disconnectedCallback();
    }

    private handleGlobalKeydown = (e: KeyboardEvent) => {
        if (e.ctrlKey && e.code === 'Space') {
            e.preventDefault();
            this.active ? this.closeSearch() : this.openSearch();
        }

        if (this.active && e.key === 'Escape') this.closeSearch();
    };

    private openSearch() {
        this.active = true;
        this.recentSearches = GlobalSearchStorage.getRecentSearches();
        this.updateResults();
        this.tryRestoreSelection();
        this.updateComplete.then(() => {
            const input = this.renderRoot.querySelector('input')
            if (input) {
                input.focus()
                input.setSelectionRange(0, input.value.length); // selecciona todo el texto para facilitar su reemplazo inmediato
            }
        });
    }

    private closeSearch() {
        this.rememberCurrentSelectionPath();
        this.active = false;
        this.selectedIndex = 0;
        this.expandedPaths = [];
    }

    private handleInput(e: InputEvent) {
        const val = (e.target as HTMLInputElement).value;
        this.query = val;
        this.updateResults();
    }

    private updateResults() {
        const normalizedQuery = this.query.trim().toLowerCase();
        this.expandedPaths = [];

        if (!normalizedQuery) {
            this.results = this.recentSearches.map((term, index) => ({
                id: `recent-${index}-${term}`,
                title: term,
                category: ESearchCategory.RECENT,
                action: () => this.useRecentSearch(term)
            }));
            this.selectedIndex = 0;
            return;
        }

        const moduleMatches: ISearchResult[] = this.providers
            .filter((p) => matchesSearchQuery(this.query, [ESearchCategory.MODULES, p.label, p.subLabel || ""]))
            .map<ISearchResult>(p => ({
                id: `module-${p.key}`,
                title: p.label,
                subtitle: p.subLabel,
                category: ESearchCategory.MODULES,
                action: () => {
                    if (p.action) {
                        p.action();
                        this.closeSearch(); // NOTE cierre forzado
                    } else {
                        this.useSuggestedModule(p.label);
                    }
                }
            }));

        const providerResults = this.providers.flatMap(provider => provider.search(this.query));

        this.results = [...moduleMatches, ...providerResults];
        this.selectedIndex = 0;
    }

    private getExpandedSet(): Set<string> {
        return new Set(this.expandedPaths);
    }

    private setExpanded(path: string, expanded: boolean) {
        const next = this.getExpandedSet();
        if (expanded) {
            next.add(path);
        } else {
            next.delete(path);
        }
        this.expandedPaths = [...next];
    }

    private setExpandedAndAdjustSelection(path: string, expanded: boolean) {
        const beforeItems = this.buildVisibleItems();
        const previousSelectedIndex = this.selectedIndex;
        const previousSelectedPath = beforeItems[previousSelectedIndex]?.path;

        this.setExpanded(path, expanded);

        const afterItems = this.buildVisibleItems();
        if (afterItems.length === 0) {
            this.selectedIndex = 0;
            return;
        }

        if (previousSelectedPath) {
            const samePathIndex = afterItems.findIndex(item => item.path === previousSelectedPath);
            if (samePathIndex >= 0) {
                this.selectedIndex = samePathIndex;
                return;
            }

            if (!expanded && previousSelectedPath.startsWith(`${path}>`)) {
                const parentIndex = afterItems.findIndex(item => item.path === path);
                if (parentIndex >= 0) {
                    this.selectedIndex = parentIndex;
                    return;
                }
            }
        }

        this.selectedIndex = Math.max(0, Math.min(previousSelectedIndex, afterItems.length - 1));
    }

    private isExpanded(path: string): boolean {
        return this.expandedPaths.includes(path);
    }

    private buildVisibleItems(): VisibleResultItem[] {
        const items: VisibleResultItem[] = [];
        const showExpandedByDefault = this.isShowingRecentSearches();

        const addItems = (results: ISearchResult[], parentPath?: string, depth = 0) => {
            results.forEach((result) => {
                const currentPath = parentPath ? `${parentPath}>${String(result.id)}` : String(result.id);
                const children = result.children ?? [];
                const hasChildren = children.length > 0;
                const expanded = hasChildren && (
                    showExpandedByDefault
                    || this.isExpanded(currentPath)
                );

                items.push({
                    path: currentPath,
                    result,
                    depth,
                    parentPath,
                    hasChildren,
                    expanded
                });

                if (hasChildren && expanded) {
                    addItems(children, currentPath, depth + 1);
                }
            });
        };

        addItems(this.results);
        return items;
    }

    private getVisibleItems(): VisibleResultItem[] {
        return this.buildVisibleItems();
    }

    private rememberCurrentSelectionPath(items = this.getVisibleItems()) {
        const selected = items[this.selectedIndex];
        if (selected?.path) {
            this.lastSelectedPath = selected.path;
        }
    }

    private tryRestoreSelection() {
        if (!this.lastSelectedPath) {
            const items = this.getVisibleItems();
            if (!items.length) {
                this.selectedIndex = 0;
                return;
            }
            this.selectedIndex = Math.max(0, Math.min(this.selectedIndex, items.length - 1));
            return;
        }

        // Expande la rama del último path para que un child recordado pueda volver a mostrarse.
        const segments = this.lastSelectedPath.split('>');
        if (segments.length > 1) {
            const ancestors: string[] = [];
            for (let i = 0; i < segments.length - 1; i++) {
                ancestors.push(segments.slice(0, i + 1).join('>'));
            }
            const next = this.getExpandedSet();
            ancestors.forEach(path => next.add(path));
            this.expandedPaths = [...next];
        }

        const items = this.getVisibleItems();
        if (!items.length) {
            this.selectedIndex = 0;
            return;
        }

        let candidatePath = this.lastSelectedPath;
        let index = items.findIndex(item => item.path === candidatePath);

        while (index < 0 && candidatePath.includes('>')) {
            candidatePath = candidatePath.slice(0, candidatePath.lastIndexOf('>'));
            index = items.findIndex(item => item.path === candidatePath);
        }

        if (index >= 0) {
            this.selectedIndex = index;
            this.scrollSelectedItemIntoView("instant");
            return;
        }

        this.selectedIndex = Math.max(0, Math.min(this.selectedIndex, items.length - 1));
        this.scrollSelectedItemIntoView("instant");
    }

    private useRecentSearch(term: string) {
        this.query = term;
        const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
        if (input) {
            input.value = term;
            input.focus();
        }
        this.updateResults();
        this.tryRestoreSelection();
    }

    private useSuggestedModule(label: string) {
        this.query = label;
        const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
        if (input) {
            input.value = label;
            input.focus();
        }
        this.updateResults();
        this.tryRestoreSelection();
    }

    private executeResult(result: ISearchResult) {
        const selectedText = this.query || result.title;
        this.dispatchEvent(new CustomEvent('global-search-select', {
            detail: {
                text: selectedText,
                result
            } as IGlobalSearchSelectEventDetail,
            bubbles: true,
            composed: true
        }));

        result.action();
        if (result.category !== ESearchCategory.RECENT) {
            GlobalSearchStorage.saveRecentSearch(selectedText);
            this.recentSearches = GlobalSearchStorage.getRecentSearches();
        }
        if (result.category !== ESearchCategory.MODULES && result.category !== ESearchCategory.RECENT) {
            this.closeSearch();
        }
    }

    private handleKeyDown(e: KeyboardEvent) {
        let shouldScrollToSelection = false;
        const visibleItems = this.getVisibleItems();
        const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
        const clearQueryButton = this.renderRoot.querySelector('.clear-query-btn') as HTMLButtonElement | null;
        const clearButton = this.renderRoot.querySelector('.clear-all-btn') as HTMLButtonElement | null;
        const totalItems = visibleItems.length + (this.isShowingRecentSearches() ? 1 : 0); // +1 for "Clear Searches" button
        const isClearQueryFocused = clearQueryButton?.classList.contains('focus-visible') ?? false;
        const selectedItem = visibleItems[this.selectedIndex];

        // Prioriza la navegación en árbol (parent/children) antes de mover foco al botón de limpiar.
        if (!isClearQueryFocused && selectedItem) {
            if (e.key === 'ArrowRight' && selectedItem.hasChildren && !selectedItem.expanded) {
                e.preventDefault();
                this.setExpandedAndAdjustSelection(selectedItem.path, true);
                shouldScrollToSelection = true;
            } else if (e.key === 'ArrowLeft') {
                if (selectedItem.hasChildren && selectedItem.expanded && !this.isShowingRecentSearches()) {
                    e.preventDefault();
                    this.setExpandedAndAdjustSelection(selectedItem.path, false);
                    shouldScrollToSelection = true;
                } else if (selectedItem.parentPath) {
                    const parentIndex = visibleItems.findIndex((item) => item.path === selectedItem.parentPath);
                    if (parentIndex >= 0) {
                        e.preventDefault();
                        this.selectedIndex = parentIndex;
                        shouldScrollToSelection = true;
                    }
                }
            }

            if (shouldScrollToSelection) {
                this.scrollSelectedItemIntoView();
                return;
            }
        }

        if (clearQueryButton && input) {
            const inputValueLength = input.value.length;
            const caretAtEnd = input.selectionStart === inputValueLength && input.selectionEnd === inputValueLength;

            if (e.key === 'ArrowRight' && !isClearQueryFocused && caretAtEnd && inputValueLength > 0) {
                e.preventDefault();
                clearQueryButton.classList.add('focus-visible');
                return;
            }

            if (isClearQueryFocused) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    clearQueryButton.click();
                    return;
                }

                if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    clearQueryButton.classList.remove('focus-visible');
                    input.focus();
                    input.setSelectionRange(0, 0);
                    return;
                }

                if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    clearQueryButton.classList.remove('focus-visible');
                    input.focus();
                    input.setSelectionRange(inputValueLength, inputValueLength);
                    return;
                }
            }

            if (e.key === 'ArrowLeft' && !isClearQueryFocused) {
                clearQueryButton.classList.remove('focus-visible');
            }
        }

        if (e.key === 'ArrowDown' && totalItems > 0) {
            this.selectedIndex = (this.selectedIndex + 1) % totalItems; // navegación infinita
            this.rememberCurrentSelectionPath(visibleItems);
            shouldScrollToSelection = true;
            e.preventDefault();
        } else if (e.key === 'ArrowUp' && totalItems > 0) {
            this.selectedIndex = (this.selectedIndex - 1 + totalItems) % totalItems; // navegación infinita
            this.rememberCurrentSelectionPath(visibleItems);
            shouldScrollToSelection = true;
            e.preventDefault();
        } else if (e.key === 'Enter' && totalItems > 0) {
            e.preventDefault();
            if (this.selectedIndex === visibleItems.length) {
                // Focus on the "Clear Searches" button
                clearButton?.click();
            } else {
                const selected = visibleItems[this.selectedIndex];
                if (selected) this.executeResult(selected.result);
            }
        }
        if (clearButton && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            if (this.selectedIndex === visibleItems.length) {
                clearButton.classList.add('focus-visible');
            } else {
                clearButton.classList.remove('focus-visible');
            }
        }

        if (shouldScrollToSelection) {
            this.scrollSelectedItemIntoView();
        }
    }

    private scrollSelectedItemIntoView(behavior: ScrollBehavior = 'smooth') {
        this.updateComplete.then(() => {
            const selectedItem = this.renderRoot.querySelector('.result-item.selected, .clear-all-btn.focus-visible') as HTMLElement | null;
            selectedItem?.scrollIntoView({ block: 'center', behavior });
        });
    }

    private clearQuery() {
        this.query = '';
        const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
        if (input) {
            input.value = '';
            input.focus();
        }
        this.updateResults();
    }

    private clearRecentSearches() {
        GlobalSearchStorage.clearRecentSearches();
        this.recentSearches = [];
        this.results = [];
        this.selectedIndex = 0;
        this.expandedPaths = [];
    }

    private isShowingRecentSearches(): boolean {
        return !this.query.trim() && this.results.length > 0 && this.results[0]?.category === ESearchCategory.RECENT;
    }

    render() {
        if (!this.active) return html``;

        const visibleItems = this.getVisibleItems();

        return html`
    <div class="overlay" @click=${() => this.closeSearch()}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
            <div class="input-container">
                ${unsafeSVG(ic_search)}
                <input
                    type="text"
                    placeholder="Busca alumnos, grupos, escuelas, módulos..."
                    .value=${this.query}
                    @input=${this.handleInput}
                    @keydown=${this.handleKeyDown}
                    autofocus
                >
                ${!!this.query ? html`
                    <button class="clear-query-btn" @click=${() => this.clearQuery()}>
                        ${unsafeSVG(ic_backspace)}
                    </button>
                `: null}
            </div>
            ${visibleItems.length > 0 ? html`
            <div class="results">
                ${repeat(visibleItems, (item) => item.path, (item, i) => html`
                <div
                    class="result-item ${item.depth > 0 ? 'is-child' : ''} ${i === this.selectedIndex ? 'selected' : ''}"
                    style=${`--depth: ${item.depth}`}
                    @click=${() => {
                this.selectedIndex = i;
                this.lastSelectedPath = item.path;
                this.executeResult(item.result);
            }}
                >
                    <div class="result-content">
                        <span class="title">${item.result.title}</span>
                        ${item.result.subtitle ? html`<span class="subtitle">${item.result.subtitle}</span>` : ''}
                        ${item.result.devSubtitle ? html`<span class="dev-subtitle">${item.result.devSubtitle}</span>` : ''}
                    </div>
                    <div class="result-actions">
                        ${item.hasChildren ? html`
                        <button
                            class="toggle-children-btn"
                            @click=${(e: Event) => {
                    e.stopPropagation();
                    this.setExpandedAndAdjustSelection(item.path, !item.expanded);
                }}
                            title=${item.expanded ? 'Colapsar' : 'Expandir'}
                            aria-label=${item.expanded ? 'Colapsar' : 'Expandir'}
                        >
                            ${item.expanded ? 'v' : '>'}
                        </button>
                        ` : ''}
                        <span class="category">${item.result.category}</span>
                    </div>
                </div>
                `)}
                ${this.isShowingRecentSearches() ? html`
                <div class="results-footer">
                    <button class="clear-all-btn" @click=${() => this.clearRecentSearches()}>
                        ${unsafeSVG(ic_trash)}
                        Limpiar búsquedas
                    </button>
                </div>
                ` : ''}
            </div>
            ` : ''}
        </div>
    </div>
    `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'app-global-search': GlobalSearchComponent;
    }

    interface HTMLElementEventMap {
        'global-search-select': CustomEvent<IGlobalSearchSelectEventDetail>;
    }
}
